import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import {
  ReconciliationService,
  type ReconciliationDTO,
  type ReconciliationLineDTO
} from '../../services/reconciliation.service';
import { ChargeService } from '../../services/charge.service';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { ChargeReadComponent } from '../charges/charge-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import type { ChargeDTO } from '../../core/models';
import { sumReconciliationLineAmounts, type ReconciliationLineTotals } from './reconciliation-line-totals';
import {
  reconciliationChargeLabel,
  reconciliationCustomerLabel,
  reconciliationSaleLabel
} from './reconciliation-linked-entities';

@Component({
  selector: 'app-reconciliation-detail',
  standalone: true,
  templateUrl: './reconciliation-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .reconciliation-lines-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.65rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
      }
      :host ::ng-deep .reconciliation-lines-table .p-datatable-tbody > tr > td {
        font-size: 0.75rem;
        vertical-align: top;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header,
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content,
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    TooltipModule,
    DrawerModule,
    ChargeReadComponent,
    SaleReadComponent,
    CustomerReadComponent
  ]
})
export class ReconciliationDetailComponent {
  private route = inject(ActivatedRoute);
  private reconciliationService = inject(ReconciliationService);
  private chargeService = inject(ChargeService);
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private toastr = inject(ToastrService);

  reconciliation = signal<ReconciliationDTO | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);

  selectedCharge = signal<ChargeDTO | null>(null);
  isChargeDetailsOpen = signal(false);
  chargeDetailsLoading = signal(false);

  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isSaleDetailsOpen = signal(false);
  saleDetailsLoading = signal(false);

  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  constructor() {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : NaN;
    if (Number.isNaN(id)) {
      this.error.set('Identificador inválido');
      this.loading.set(false);
      return;
    }
    this.reconciliationService.getById(id).subscribe({
      next: res => {
        if (res.ok && res.body) {
          this.reconciliation.set(res.body);
        } else {
          this.error.set(res.message || 'No encontrado');
        }
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Error al cargar el detalle');
        this.loading.set(false);
      }
    });
  }

  lineTotals(lines: ReconciliationLineDTO[] | undefined | null): ReconciliationLineTotals {
    return sumReconciliationLineAmounts(lines);
  }

  saleLabel(line: ReconciliationLineDTO): string {
    return reconciliationSaleLabel(line);
  }

  chargeLabel(line: ReconciliationLineDTO): string {
    return reconciliationChargeLabel(line);
  }

  customerLabel(line: ReconciliationLineDTO): string {
    return reconciliationCustomerLabel(line);
  }

  openChargeDetails(charge: ChargeDTO | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!charge?.id || this.chargeDetailsLoading()) return;
    this.chargeDetailsLoading.set(true);
    this.chargeService.searchCharges({ id: charge.id }, { page: 0, size: 1 }).subscribe({
      next: response => {
        this.chargeDetailsLoading.set(false);
        this.selectedCharge.set(response.body?.[0] ?? charge);
        this.isChargeDetailsOpen.set(true);
      },
      error: () => {
        this.chargeDetailsLoading.set(false);
        this.selectedCharge.set(charge);
        this.isChargeDetailsOpen.set(true);
      }
    });
  }

  onChargeDrawerVisibleChange(visible: boolean): void {
    this.isChargeDetailsOpen.set(visible);
    if (!visible) this.selectedCharge.set(null);
  }

  openSaleDetails(saleId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: response => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: chargesResp => this.saleCharges.set(chargesResp.body ?? []),
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle de la venta');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
      }
    });
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  openSaleDetailsFromCustomer(saleId: number): void {
    this.openSaleDetails(saleId);
  }

  openCustomerDetails(customerId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: response => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  openCep(url?: string | null): void {
    const u = url?.trim();
    if (u) {
      window.open(u, '_blank', 'noopener,noreferrer');
    }
  }

  /** Misma lógica que en el listado: solo fecha sin hora. */
  transactionDateOnly(raw?: string | null): string {
    if (raw == null || String(raw).trim() === '') {
      return '—';
    }
    const s = String(raw).trim();
    if (s.includes('T')) {
      return s.split('T')[0] ?? s;
    }
    const space = s.indexOf(' ');
    if (space > 0) {
      return s.substring(0, space);
    }
    return s;
  }
}
